//* Vue
import type { SetupContext } from "vue";
//* 组件库
import type { UiTypes, UiInput } from "various-ui";
//* 组件属性
import type { DefaultSearchProps, DefaultSearchEmits } from "../index";
//* 样式
import css from "../style/index.module.less";
export const useComposable = (define: DefaultSearchProps, emits: SetupContext<typeof DefaultSearchEmits>["emit"]) => {
    //* 获取i18n
    const i18n = useI18n();

    //* 静态数据
    const vars = {
        timer: <NodeJS.Timeout | undefined>undefined,
        controller: <AbortController | undefined>undefined,
    };

    //* 响应式数据
    const refs = {
        candidates: ref<UiTypes.candidate[]>([]),
        keyword: ref<string>(define.modelValue),
        visible: ref<boolean>(false),
        data: ref<UiTypes.candidate>(),
    };

    //* 节点
    const nodes = {
        input: ref<InstanceType<typeof UiInput>>(),
    };

    //* 监听器
    const watchs = {
        stop: watch(
            () => define.modelValue,
            () => {
                refs.keyword.value = define.modelValue;
            }
        ),
    };

    //* 计算属性
    const computeds: { candidates: ComputedRef<UiTypes.candidate[]>; visible: ComputedRef<boolean> } = {
        visible: computed(() => !!computeds.candidates.value.length && refs.visible.value),
        candidates: computed(() => {
            switch (define.mode) {
                case "default":
                    return define.option || [];
                case "select":
                    return refs.candidates.value;
                default:
                    return [];
            }
        }),
    };

    //* 动态属性
    const binds = reactive({
        //* 候选项
        candidates: computed(() => ({
            style: {
                maxHeight: define.height + "px",
                zIndex: define.zIndex,
                width: define.widthExtra ? define.widthExtra + "px" : "100%",
            },
        })),

        //* 容器
        container: computed(() => (css[define.mode] ? { class: `${css[define.mode]}` } : {})),
        //* 主体
        main: computed(() => ({
            name: define.name,
            size: define.size,
            width: define.width,
            zIndex: define.zIndex,
            disabled: define.disabled,
            readonly: define.mode == "click",
            maxlength: define.maxlength,
            placeholder: define.placeholder || i18n.t("Layout.InputTypeSearch", { type: "Keyword" }),
        })),
    });

    //* 响应事件
    const ons = {
        //* 容器
        container: () => {
            if (define.mode == "click") {
                return { click: () => emits("search") };
            } else {
                return {};
            }
        },

        //* 图标
        icon: () => {
            switch (define.mode) {
                case "default":
                    return { click: () => emits("search") };
                case "select":
                    return { click: () => nodes.input.value?.focus() };
                default:
                    return {};
            }
        },

        //* 主体
        main: () => {
            switch (define.mode) {
                case "default":
                    return {
                        blur: () => {
                            refs.visible.value = false;
                            emits("blur");
                        },
                        focus: () => {
                            refs.visible.value = true;
                            emits("focus");
                        },
                        enter: () => emits("search"),
                        input: () => {
                            emits("update:modelValue", refs.keyword.value);
                            emits("input");
                        },
                        select: (_evt: Event, option: UiTypes.candidate) => {
                            emits("update:modelValue", refs.keyword.value);
                            emits("search", option);
                        },
                    };
                case "select":
                    return {
                        focus: () => {
                            //* 状态更新
                            refs.visible.value = true;
                            //* 数据获取
                            methods.candidate();
                        },
                        input: () => {
                            //* 事件响应
                            emits("update:modelValue", refs.keyword.value);
                            //* 触发候选项查询
                            methods.cancel();
                            methods.candidate();
                        },
                        blur: () => {
                            //* 状态更新
                            refs.visible.value = false;
                            //* 请求取消
                            methods.cancel();
                            //* 检测是否需要恢复上一次选择的内容
                            if (refs.keyword.value && refs.data.value && refs.data.value.value != refs.keyword.value) {
                                //* 数据更新
                                refs.keyword.value = refs.data.value.value;
                                //* 事件响应
                                emits("update:modelValue", refs.keyword.value);
                            }
                        },
                    };

                default:
                    return {};
            }
        },
    };

    const methods = {
        //* 取消事件
        cancel: () => {
            //* 移除定时器
            vars.timer && clearTimeout(vars.timer);
            vars.timer = undefined;

            //* 取消接口请求
            vars.controller && vars.controller.abort();
            vars.controller = undefined;
        },

        //* 状态重置
        reset: (option?: { keyword: string }) => {
            refs.data.value = undefined;
            refs.keyword.value = option?.keyword || "";
            refs.candidates.value = [];
        },

        //* 选择事件
        select: (option: UiTypes.candidate) => {
            if (define.mode == "select") {
                //* 数据缓存
                refs.data.value = option;
                //* 事件响应
                emits("update:modelValue", refs.keyword.value);
            } else {
                emits("update:modelValue", "");
            }

            //* 事件响应
            emits("search", option);
        },

        //* 触发候选项查询
        candidate: () => {
            const keyword = refs.keyword.value?.trim();
            if (!keyword) {
                refs.candidates.value = [];
            } else {
                //* 接口请求
                vars.timer = setTimeout(() => {
                    vars.controller = new AbortController();
                    api.frontend("/api/search/suggest", {
                        method: "POST",
                        data: Object.assign(define.query, { mode: define.type, keyword: keyword, result_type: "symbol" }),
                        signal: vars.controller?.signal,
                        success: (response: { data: any[]; datas: any[] }) => {
                            refs.candidates.value = [];
                            response?.data?.forEach((value: any) => {
                                const result: UiTypes.candidate = { id: value.rel_id, value: value.text, label: value.text };
                                switch (define.type) {
                                    case "gene": {
                                        result.type = value.rel_tax_id;
                                        break;
                                    }

                                    case "model": {
                                        result.label = useModelNameAnalyze(result.label);
                                        break;
                                    }
                                }
                                if (define.alias) result.alias = value.alias || [];

                                refs.candidates.value.push(result);
                            });
                        },
                    });
                }, 200);
            }
        },
    };

    return { ons, refs, nodes, binds, watchs, methods, vars, computeds };
};
